import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './Footer.module.scss'
import commonStyle from '../../style.module.scss'
import phoneIcon from '../assets/icons/phone.svg'
import help24x7Icon from '../assets/icons/help-24-7.svg'
import facebookIcon from '../assets/icons/facebook.svg'
import youtubeIcon from '../assets/icons/youtube.svg'
import instagramIcon from '../assets/icons/instagram.svg'
import tiktokIcon from '../assets/icons/tiktok.svg'
import appStoreBadge from '../assets/images/apple-store-badge.jpg'
import googlePlayBadge from '../assets/images/google_play_web_generic.png'
import pharmacyBadge from '../assets/images/pharmacy.jpg'
import legitscriptBadge from '../assets/images/436720.png'
import accreditedBadge from '../assets/images/compounding-pharmacy.png'
import privacyChoicesIcon from '../assets/icons/privacy-choices.svg'
import usFlag from '../assets/icons/us-flag.svg'

interface IFooterProps {
  scrollTop: () => void
  onHomeClick: () => void
  showHint: () => void
  closeSimulation: () => void
}

const FOOTER_LINKS = [
  'footerAbout',
  'footerInvestor',
  'footerAffiliates',
  'footerJobs',
  'footerHelp',
  'footerLearn',
  'footerGiveBack',
  'footerGiftCards',
  'footerSellOnChewy'
]

const LEGAL_LINKS = ['legalTerms', 'legalPrivacy', 'legalAds', 'legalAccessibility', 'legalSupplyChains', 'legalVendor']

const Footer = ({ scrollTop, onHomeClick, showHint, closeSimulation }: IFooterProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()

  // Mobile app bottom tab bar (Target Footer pattern). Shop is the active tab and
  // scrolls to top; Care / Autoship / Account are decorative — no destination in the sim.
  if (viewport.mobile)
    return (
      <footer className={style.footerMobile} data-testid="SW-EC-Footer">
        <button type="button" className={`${style.tab} ${style.tabActive}`} onClick={onHomeClick}>
          <i className={`pi pi-shopping-bag ${style.tabIcon}`} />
          <span className={style.tabText}>{t('questions', 'ec', 'chewy', 'tabShop')}</span>
        </button>
        <EmptyLink color="#1C49C4">
          <div className={style.tab}>
            <i className={`pi pi-sync ${style.tabIcon}`} />
            <span className={style.tabText}>{t('questions', 'ec', 'chewy', 'autoship')}</span>
          </div>
        </EmptyLink>
        <EmptyLink color="#1C49C4">
          <div className={style.tab}>
            <i className={`pi pi-user ${style.tabIcon}`} />
            <span className={style.tabText}>{t('questions', 'ec', 'chewy', 'tabAccount')}</span>
          </div>
        </EmptyLink>
        {/* <div className={style.tab}> */}
        <div className={`${commonStyle.IconAytmPlace} ${style.aytmTab}`}>
          <i className="icon-aytmfd-help" onClick={showHint} />
        </div>
        {/* </div> */}
        {/* <div className={style.tab}> */}
        <div className={`${commonStyle.IconAytmPlace} ${style.aytmTab}`}>
          <i className="icon-aytmfd-exit" onClick={closeSimulation} />
        </div>
        {/* </div> */}
      </footer>
    )

  return (
    <footer className={style.footer} data-testid="SW-EC-Footer">
      <div className={style.topStrip}>
        <div className={style.topStripInner}>
          <span className={style.topText}>{t('questions', 'ec', 'chewy', 'experts247')}</span>
          <EmptyLink color="#1C49C4">
            <span className={style.topItem}>
              <img src={phoneIcon} alt="" role="presentation" className={style.topItemIcon} />
              <span>{t('questions', 'ec', 'chewy', 'phone')}</span>
            </span>
          </EmptyLink>
          <EmptyLink color="#1C49C4">
            <span className={style.topItem}>
              <img src={help24x7Icon} alt="" role="presentation" className={style.topItemIcon} />
              <span>{t('questions', 'ec', 'chewy', 'chatNow')}</span>
            </span>
          </EmptyLink>
          <button type="button" className={style.backToTop} onClick={scrollTop}>
            <i className="pi pi-arrow-up" />
            <span>{t('questions', 'ec', 'chewy', 'backToTop')}</span>
          </button>
        </div>
      </div>

      <div className={style.middle}>
        {/* US-only store: a static region indicator (local flag asset), not a country selector. */}
        <div className={style.country}>
          <img src={usFlag} alt="" role="presentation" className={style.flag} />
          <span>{t('questions', 'ec', 'chewy', 'unitedStates')}</span>
        </div>
        <nav className={style.links}>
          {FOOTER_LINKS.map(key => (
            <EmptyLink key={key} color="#1C49C4">
              <span className={style.link}>{t('questions', 'ec', 'chewy', key)}</span>
            </EmptyLink>
          ))}
        </nav>
        <div className={style.social}>
          <EmptyLink color="#1C49C4">
            <img src={facebookIcon} alt="" role="presentation" className={style.socialIcon} />
          </EmptyLink>
          <EmptyLink color="#1C49C4">
            <img src={youtubeIcon} alt="" role="presentation" className={style.socialIcon} />
          </EmptyLink>
          <EmptyLink color="#1C49C4">
            <img src={instagramIcon} alt="" role="presentation" className={style.socialIcon} />
          </EmptyLink>
          <EmptyLink color="#1C49C4">
            <img src={tiktokIcon} alt="" role="presentation" className={style.socialIcon} />
          </EmptyLink>
        </div>
      </div>

      <div className={style.badgesRow}>
        <EmptyLink color="#1C49C4">
          <img src={appStoreBadge} alt={t('questions', 'ec', 'chewy', 'altAppStore')} className={style.badge} />
        </EmptyLink>
        <EmptyLink color="#1C49C4">
          <img src={googlePlayBadge} alt={t('questions', 'ec', 'chewy', 'altGooglePlay')} className={style.badge} />
        </EmptyLink>
        <EmptyLink color="#1C49C4">
          <img src={pharmacyBadge} alt={t('questions', 'ec', 'chewy', 'altPharmacy')} className={style.badge} />
        </EmptyLink>
        <EmptyLink color="#1C49C4">
          <img src={legitscriptBadge} alt={t('questions', 'ec', 'chewy', 'altLegitScript')} className={style.badge} />
        </EmptyLink>
        <EmptyLink color="#1C49C4">
          <img src={accreditedBadge} alt={t('questions', 'ec', 'chewy', 'altAccredited')} className={style.badge} />
        </EmptyLink>
      </div>

      <div className={style.legal}>
        <span className={style.legalCopy}>{t('questions', 'ec', 'chewy', 'copyright')}</span>
        {LEGAL_LINKS.map(key => (
          <EmptyLink key={key} color="#1C49C4">
            <span className={style.legalLink}>{t('questions', 'ec', 'chewy', key)}</span>
          </EmptyLink>
        ))}
        <EmptyLink color="#1C49C4">
          <span className={style.legalLink}>
            <img src={privacyChoicesIcon} alt="" role="presentation" className={style.privacyIcon} />
            {t('questions', 'ec', 'chewy', 'privacyChoices')}
          </span>
        </EmptyLink>
      </div>
    </footer>
  )
}

export default Footer
